import { createContext, useContext, useMemo, type ReactNode } from 'react';
import enMessages from '../../messages/en.json';

type Messages = typeof enMessages;

function getNestedValue(obj: Record<string, unknown>, path: string): string | undefined {
  const parts = path.split('.');
  let current: unknown = obj;
  for (const part of parts) {
    if (current == null || typeof current !== 'object') return undefined;
    current = (current as Record<string, unknown>)[part];
  }
  return typeof current === 'string' ? current : undefined;
}

function parseRich(
  template: string,
  tags: Record<string, (chunks: ReactNode) => ReactNode>
): ReactNode {
  const parts: ReactNode[] = [];
  const regex = /<(\w+)>(.*?)<\/\1>/gs;
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  let key = 0;

  while ((match = regex.exec(template)) !== null) {
    if (match.index > lastIndex) {
      parts.push(template.slice(lastIndex, match.index));
    }
    const tagName = match[1];
    const inner = match[2];
    const renderer = tags[tagName];
    parts.push(renderer ? renderer(parseRich(inner, tags)) : inner);
    lastIndex = regex.lastIndex;
  }

  if (lastIndex < template.length) {
    parts.push(template.slice(lastIndex));
  }

  return parts.length === 1 ? parts[0] : <>{parts.map((p) => <span key={key++}>{p}</span>)}</>;
}

export type Translator = {
  (key: string, values?: Record<string, string | number>): string;
  rich: (key: string, tags: Record<string, (chunks: ReactNode) => ReactNode>) => ReactNode;
};

function createTranslator(messages: Messages, namespace?: string): Translator {
  const translate = (key: string, values?: Record<string, string | number>) => {
    const fullKey = namespace ? `${namespace}.${key}` : key;
    let text = getNestedValue(messages as Record<string, unknown>, fullKey) ?? fullKey;
    if (values) {
      for (const [k, v] of Object.entries(values)) {
        text = text.replace(new RegExp(`\\{${k}\\}`, 'g'), String(v));
      }
    }
    return text;
  };

  translate.rich = (key: string, tags: Record<string, (chunks: ReactNode) => ReactNode>) => {
    const fullKey = namespace ? `${namespace}.${key}` : key;
    const template = getNestedValue(messages as Record<string, unknown>, fullKey) ?? fullKey;
    return parseRich(template, tags);
  };

  return translate as Translator;
}

const I18nContext = createContext({ locale: 'en', messages: enMessages as Messages });

export function I18nProvider({ children }: { children: ReactNode }) {
  const value = useMemo(() => ({ locale: 'en', messages: enMessages as Messages }), []);
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useLocale() {
  return useContext(I18nContext).locale;
}

export function useTranslations(namespace?: string): Translator {
  const { messages } = useContext(I18nContext);
  return useMemo(() => createTranslator(messages, namespace), [messages, namespace]);
}

// Server API compatibility (unused in Vite; hooks used instead)
export async function getTranslations(opts?: { locale?: string; namespace?: string }) {
  return createTranslator(enMessages as Messages, opts?.namespace);
}

export function setRequestLocale(_locale: string) {
  // no-op in SPA
}

export async function getMessages() {
  return enMessages;
}
